'use client';
import { useId, useRef, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import { Label } from '../ui/shadcn/label';
import { Button } from '../ui/Button';
import { IconButton } from '../ui/IconButton';
import { Input } from '../ui/Input';
import { useOperationFailure, useToast } from '../ui/Toast';
import { Spinner } from '../ui/states';
import { useCopyAction } from '../../lib/useCopyAction';
import { ElowenApiError, elowenClient, type LicenceStatusView } from '../../lib/elowenClient';
import { useTranslation } from '../../lib/i18n';
import { holdsAnswer, releasing } from '../../lib/saveFeedback';
import type { LocaleDict } from '../../lib/i18n/types';

/** Shared by the locked page and the Settings licence rows so both read and write the same cached view. */
export const LICENCE_QUERY_KEY = ['licence-status'];
export const STARTER_OFFER_QUERY_KEY = ['licence', 'starter'] as const;
const ACTIVATION_FAILURE_KEY = 'licence-activate';

/** Keys are Crockford base32 without spaces; accept whatever the visitor pasted and normalize. */
function normalizeActivationKey(key: string): string {
  return key.replace(/\s+/g, '').toUpperCase();
}

/** The codes `POST /licence/activate` answers with when the licensing server refuses the key (403) or the key is
 *  bound to another server (409). A bare `forbidden` is the daemon refusing the visitor, not the key. */
const KEY_REFUSALS: ReadonlySet<string> = new Set(['invalidKey', 'revoked', 'expired', 'notActive']);
const KEY_BOUND_ELSEWHERE = 'boundElsewhere';

/** What the daemon says about the key the visitor typed: malformed, refused by the licensing server, or already
 *  active on another server. Each names the value, so it stays with the input. */
function keyProblem(e: unknown, t: LocaleDict): string | null {
  if (!(e instanceof ElowenApiError)) return null;
  if (e.status === 400) return t.licence.errorBadKey;
  if (e.status === 403 && e.code !== undefined && KEY_REFUSALS.has(e.code)) return t.licence.errorForbidden;
  if (e.status === 409 && e.code === KEY_BOUND_ELSEWHERE) return t.licence.errorConflict;
  return null;
}

/** Any other failure (lost authorization, the licensing server cannot be reached, an unexpected answer) says nothing about the key:
 *  it is the activation command that failed, so it is a toast. */
function activationRefusal(e: unknown, t: LocaleDict): string {
  return e instanceof ElowenApiError && e.status === 502 ? t.licence.errorServer : t.common.actionFailed;
}

/** The key input with its Activate button, shared by the locked page and the Settings replace dialog:
 *  one input, one error mapping, one success contract (the fresh status view the daemon answered).
 *
 *  An activation whose outcome is unknown (the connection dropped around the request) may already have been
 *  applied, and the daemon may be restarting because of it. It HOLDS the form, button and Enter alike, until the
 *  licence status was read again. That read adopts only what it proves: an instance that was locked and is active
 *  now, or a different licence than the one the activation started from. A newer token proves nothing (the periodic
 *  check-in renews the current licence's token too), so then the read only refreshes what is shown, the hold ends
 *  and the key stays in the field for the person to send again. A read that fails verifies nothing and keeps the hold. */
export function LicenceActivateForm({ onSuccess }: { onSuccess: (view: LicenceStatusView) => void }) {
  const { t } = useTranslation();
  const { toast, dismissKey } = useToast();
  const reportFailure = useOperationFailure();
  const queryClient = useQueryClient();
  const inputId = useId();
  const [key, setKey] = useState('');
  const [pending, setPending] = useState(false);
  const [held, setHeld] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  // The status the activation started from: a replaced key leaves the licence active, so "active" alone does not
  // say the new key took effect; another licence (or leaving the locked state) does, a renewed token does not.
  const before = useRef<LicenceStatusView | undefined>(undefined);

  const succeeded = (view: LicenceStatusView): void => {
    void queryClient.invalidateQueries({ queryKey: STARTER_OFFER_QUERY_KEY });
    onSuccess(view);
  };

  const verify = releasing(async () => {
    // Straight from the daemon, not through the shared query: a failed read there would replace this form with the
    // page's error state and lose the hold.
    const view = await elowenClient.licenceStatus();
    const start = before.current;
    const adopted = start !== undefined && view.state === 'active' && (start.state !== 'active' || start.licenceId !== view.licenceId);
    if (adopted) succeeded(view);
    else queryClient.setQueryData(LICENCE_QUERY_KEY, view);
  }, () => { setHeld(false); dismissKey(ACTIVATION_FAILURE_KEY); });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (pending || held) return;
    setPending(true);
    setProblem(null);
    before.current = queryClient.getQueryData<LicenceStatusView>(LICENCE_QUERY_KEY);
    elowenClient.licenceActivate(normalizeActivationKey(key)).then(
      (view) => { succeeded(view); },
      (e: unknown) => {
        const keyIssue = keyProblem(e, t);
        if (keyIssue) setProblem(keyIssue);
        else if (holdsAnswer(e)) {
          setHeld(true);
          reportFailure(ACTIVATION_FAILURE_KEY, e, { message: activationRefusal(e, t), reload: verify });
        } else toast(activationRefusal(e, t), 'error', { key: ACTIVATION_FAILURE_KEY, error: e });
        setPending(false);
      },
    );
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-2">
      <Label htmlFor={inputId}>{t.licence.keyLabel}</Label>
      <Input
        id={inputId}
        value={key}
        onChange={(event) => { setKey(event.target.value); setProblem(null); }}
        placeholder={t.licence.keyPlaceholder}
        autoComplete="off"
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        disabled={pending || held}
        aria-invalid={problem !== null}
        aria-describedby={problem ? `${inputId}-error` : undefined}
      />
      {problem ? (
        <p id={`${inputId}-error`} role="alert" className="text-ui text-destructive">{problem}</p>
      ) : null}
      <Button type="submit" variant="accent" disabled={pending || held || key.trim() === ''}>
        {pending ? <Spinner size="xs" /> : null}
        {pending ? t.licence.activating : t.licence.activate}
      </Button>
    </form>
  );
}

/** A licence identifier in mono with its copy button, shared by the locked page and the Settings
 *  licence rows. On a phone the id wraps rather than truncates, so all of it stays visible; from `sm`
 *  it stays on one line, so a narrow control column cannot strand its last character on a line of its own. */
export function CopyableId({ value, copyLabel }: { value: string; copyLabel: string }) {
  const copy = useCopyAction();
  return (
    <span className="flex min-w-0 items-center gap-3">
      <code className="min-w-0 break-all font-mono text-small text-foreground sm:shrink-0 sm:whitespace-nowrap">{value}</code>
      <IconButton
        icon={Copy}
        label={copyLabel}
        onClick={() => void copy(value, { key: `copy:licence:${copyLabel}` })}
      />
    </span>
  );
}
